import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Flex, Text, Textarea } from "@chakra-ui/react";

const MAX_LENGTH = 500;
const COUNTER_THRESHOLD = 400;
const MAX_TEXTAREA_HEIGHT_PX = 96;

const SendIcon = ({ color }) => (
  <Box
    as="svg"
    width="16px"
    height="16px"
    viewBox="0 0 24 24"
    color={color}
    fill="currentColor"
  >
    <path d="M2 21l21-9L2 3v7l15 2-15 2v7z" />
  </Box>
);

const ChatComposer = ({ onSend, isDisabled }) => {
  const [draft, setDraft] = useState("");
  const textareaRef = useRef(null);

  const adjustHeight = useCallback(() => {
    const element = textareaRef.current;
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${Math.min(
      element.scrollHeight,
      MAX_TEXTAREA_HEIGHT_PX
    )}px`;
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [draft, adjustHeight]);

  const trimmedDraft = draft.trim();
  const canSend = trimmedDraft.length > 0 && !isDisabled;

  const handleSend = () => {
    if (!canSend) return;
    onSend(trimmedDraft);
    setDraft("");
  };

  const handleChange = (event) => {
    setDraft(event.target.value.slice(0, MAX_LENGTH));
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <Box borderTop="1px solid" borderColor="dark.400" pt="8px" pb="10px">
      {draft.length >= COUNTER_THRESHOLD && (
        <Text fontSize="10px" color="dark.200" textAlign="right" mb="4px">
          {draft.length}/{MAX_LENGTH}
        </Text>
      )}
      <Flex gap="6px" align="flex-end">
        <Textarea
          ref={textareaRef}
          variant="chatComposer"
          rows={1}
          resize="none"
          value={draft}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Message everyone…"
          maxLength={MAX_LENGTH}
          isDisabled={isDisabled}
          minH="34px"
          maxH={`${MAX_TEXTAREA_HEIGHT_PX}px`}
          py="8px"
          px="10px"
        />
        <Flex
          as="button"
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          width="32px"
          height="32px"
          flex="none"
          borderRadius="6px"
          align="center"
          justify="center"
          bg={canSend ? "dark.500" : "transparent"}
          cursor={canSend ? "pointer" : "default"}
        >
          <SendIcon color={canSend ? "blue.500" : "dark.300"} />
        </Flex>
      </Flex>
    </Box>
  );
};

export default ChatComposer;
